import * as DOMPurify from "dompurify"

/**
 * Email HTML is rendered only inside an iframe with this sandbox: no `allow-scripts` and no
 * `allow-same-origin`, so the content gets an opaque origin and cannot run code or reach the app.
 * Links may still open (in a new tab, see the injected `<base target="_blank">`).
 */
export const EMAIL_PREVIEW_SANDBOX = "allow-popups allow-popups-to-escape-sandbox"

// Email clients drop these too; keeping them out also keeps nested browsing contexts and form posts out of the frame.
const FORBID_TAGS = ["iframe", "frame", "frameset", "object", "embed", "form", "base", "meta", "link"]

/**
 * Builds the `srcDoc` for the sandboxed email preview frame. The input is used as-is: entities are
 * NOT decoded, so escaped text such as `&lt;iframe&gt;` stays text.
 */
export const buildEmailPreviewDocument = (html: string): string => {
    const sanitized = DOMPurify.sanitize(html ?? "", {
        WHOLE_DOCUMENT: true,
        FORBID_TAGS
    }) as string

    const doc = new DOMParser().parseFromString(sanitized, "text/html")
    const meta = doc.createElement("meta")
    meta.setAttribute("charset", "utf-8")
    const base = doc.createElement("base")
    base.setAttribute("target", "_blank")
    doc.head.prepend(meta, base)

    // Explicit rather than relying on the browser's implied noopener for _blank: the new tab gets no
    // handle back to the preview, and the destination does not learn the app URL via the Referer.
    doc.querySelectorAll("a[href], area[href]").forEach((link) => {
        link.setAttribute("target", "_blank")
        link.setAttribute("rel", "noopener noreferrer")
    })

    return `<!DOCTYPE html>${doc.documentElement.outerHTML}`
}
